import { safeNextPath } from './safeNextPath'
import { parseSessionEndedReason, type SessionEndedReason } from '../contexts/refresh-session'

// LoginSearch keeps every field optional so other routes can navigate to the
// login page with just a `next` (the SSO/token fields are only present when a
// social-login callback redirects back here). Shared by `/login` and
// `/$org/login` (spec 2026-10-14-22).
export interface LoginSearch {
  next?: string
  email?: string
  password?: string
  access_token?: string
  refresh_token?: string
  expires_in?: number
  error?: string
  error_description?: string
  // Two-step verification (spec 2026-10-01-03): an SSO callback or a
  // password reset that still needs the second factor.
  mfa_challenge?: string
  mfa_enrollment_required?: string
  // Why the previous session ended (spec 2026-10-11-37), set by the
  // session-expired banner's link.
  reason?: SessionEndedReason
}

export function validateLoginSearch(search: Record<string, unknown>): LoginSearch {
  return {
    next: (search.next as string) || undefined,
    email: (search.email as string) || undefined,
    password: (search.password as string) || undefined,
    // Populated when a social-login (OAuth) callback redirects back here.
    access_token: (search.access_token as string) || undefined,
    refresh_token: (search.refresh_token as string) || undefined,
    expires_in: search.expires_in ? Number(search.expires_in) : undefined,
    error: (search.error as string) || undefined,
    error_description: (search.error_description as string) || undefined,
    mfa_challenge: (search.mfa_challenge as string) || undefined,
    mfa_enrollment_required: (search.mfa_enrollment_required as string) || undefined,
    reason: parseSessionEndedReason(search.reason) ?? undefined,
  }
}

// Top-level paths that are never an organization code: a `next` pointing at
// one gives no hint.
const NON_ORG_SEGMENTS = new Set([
  'login', 'admin', 'organizations', 'no-organization', 'mfa-setup', 'mfa-verify',
  'about', 'api', 'openapi.yaml', 'llms.txt', 'docs_old.html', 'impersonate', 'invitations', 'test', 'c', 'q', 'r', 's', 'v', 'wo',
])

/**
 * The organization hint of the org-less login page: the first path segment of
 * `next` (`/agi3/assets` hints `agi3`), or '' when there is none.
 */
export function orgHintFromNext(next: string | null | undefined): string {
  const safe = safeNextPath(next)
  if (!safe) return ''
  const segment = safe.split(/[/?#]/)[1] ?? ''
  if (!segment || NON_ORG_SEGMENTS.has(segment)) return ''
  try {
    return decodeURIComponent(segment)
  } catch {
    return ''
  }
}

// The key under which the login page leaves the redirect notice for the
// organization layout to show after navigating.
export const LOGIN_REDIRECT_NOTICE_KEY = 'loginRedirectedNotice'

export interface LoginRedirectNotice {
  from: string
  to: string
}

export function storeLoginRedirectNotice(notice: LoginRedirectNotice): void {
  try {
    sessionStorage.setItem(LOGIN_REDIRECT_NOTICE_KEY, JSON.stringify(notice))
  } catch {
    /* storage unavailable: no notice */
  }
}

export function readLoginRedirectNotice(org: string): LoginRedirectNotice | null {
  try {
    const raw = sessionStorage.getItem(LOGIN_REDIRECT_NOTICE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LoginRedirectNotice
    return parsed && parsed.to === org && typeof parsed.from === 'string' ? parsed : null
  } catch {
    return null
  }
}

export function clearLoginRedirectNotice(): void {
  try {
    sessionStorage.removeItem(LOGIN_REDIRECT_NOTICE_KEY)
  } catch {
    /* ignore */
  }
}
